import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const TicketTrackingDashboard = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusOptions = [
    { value: 'all',        label: 'All Statuses' },
    { value: 'open',       label: 'Open' },
    { value: 'in-progress',label: 'In Progress' },
    { value: 'waiting',    label: 'Waiting for Response' },
    { value: 'resolved',   label: 'Resolved' },
    { value: 'closed',     label: 'Closed' }
  ];

  useEffect(() => {
    if (!user) return;
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('support_tickets')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        if (!error) setTickets(data || []);
      } catch (e) {
        console.error('Failed to load tickets:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [user]);

  const getStatusColor = (status) => {
    const colors = {
      open:         isDarkMode ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'   : 'bg-blue-100 text-blue-700 border-blue-200',
      'in-progress':isDarkMode ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-yellow-100 text-yellow-700 border-yellow-200',
      waiting:      isDarkMode ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-orange-100 text-orange-700 border-orange-200',
      resolved:     isDarkMode ? 'bg-green-500/10 text-green-400 border-green-500/20'  : 'bg-green-100 text-green-700 border-green-200',
      closed:       isDarkMode ? 'bg-gray-500/10 text-gray-400 border-gray-500/20'    : 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return colors[status] || colors.open;
  };

  const getPriorityColor = (priority) => ({
    low: 'text-gray-500', medium: 'text-blue-500', high: 'text-orange-500', critical: 'text-red-500'
  }[priority] || 'text-blue-500');

  const filteredTickets = tickets.filter((t) => {
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchesSearch = !searchQuery ||
      t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.ticket_ref?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input type="search" placeholder="Search by ticket ref or subject..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>
        <div className="w-full md:w-64">
          <Select options={statusOptions} value={filterStatus} onChange={setFilterStatus} placeholder="Filter by status" />
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className={`p-12 rounded-lg border text-center ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-gray-50 border-gray-200'}`}>
          <Icon name="Ticket" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {searchQuery || filterStatus !== 'all' ? 'No tickets match your filters' : 'No support tickets yet'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {searchQuery || filterStatus !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Submit a ticket above and it will appear here once created.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => setSelectedTicket(ticket)}
              className={`p-6 rounded-lg border transition-all cursor-pointer ${
                isDarkMode
                  ? 'bg-[#0f1410] border-[#1a2820] hover:border-[#10b981]/50'
                  : 'bg-white border-gray-200 hover:border-[#107570]/50 hover:shadow-md'
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-mono text-muted-foreground">{ticket.ticket_ref}</span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(ticket.status)}`}>
                      {ticket.status?.replace('-', ' ').toUpperCase()}
                    </span>
                    <Icon name="AlertCircle" size={16} className={getPriorityColor(ticket.priority)} />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{ticket.subject}</h3>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1"><Icon name="Tag" size={14} />{ticket.category}</div>
                    <div className="flex items-center gap-1"><Icon name="Calendar" size={14} />Submitted {formatDate(ticket.created_at)}</div>
                  </div>
                </div>
                <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl rounded-lg shadow-xl max-h-[85vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-[#0a0f0d]' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-[#1a2820]' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-mono text-muted-foreground">{selectedTicket.ticket_ref}</span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status?.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-foreground mb-1">{selectedTicket.subject}</h2>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Icon name="Tag" size={12} />{selectedTicket.category}</span>
                    <span className="flex items-center gap-1"><Icon name="AlertCircle" size={12} className={getPriorityColor(selectedTicket.priority)} />{selectedTicket.priority} priority</span>
                    <span className="flex items-center gap-1"><Icon name="Calendar" size={12} />Submitted {formatDate(selectedTicket.created_at)}</span>
                  </div>
                </div>
                <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-accent rounded-lg transition-colors">
                  <Icon name="X" size={20} className="text-muted-foreground" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-sm font-semibold text-foreground mb-3">Description</h3>
              <p className={`text-sm leading-relaxed whitespace-pre-wrap p-4 rounded-lg ${isDarkMode ? 'bg-[#0f1410]' : 'bg-gray-50'}`}>
                {selectedTicket.description}
              </p>
              <div className={`mt-6 p-4 rounded-lg border ${isDarkMode ? 'bg-[#0f1410] border-[#1a2820]' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-foreground font-medium mb-1">Your ticket has been received</p>
                    <p className="text-sm text-muted-foreground">Our support team will review it and respond within one business day. You'll receive an email update when there's a reply.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketTrackingDashboard;
